import React, {useEffect, useState} from 'react';
import AgyleAccordion from '../../../../../components/ui/accordion/agyleAccordion';
import {contextFormsList} from '../../../../patient/patientContext/components/forms/formsContextList';
import {useDispatch, useSelector} from 'react-redux';
import {additionalData} from '../../../../../store/reducers/additionalData';
import _ from 'lodash';
import {getFormCustomTitle} from '../../../../../util/patientContextUtil';
import {ctxPatientAgeType, ctxPatientType, openPatientForm} from '../../../../../store/reducers/patientContext';
import makeStyles from '@mui/styles/makeStyles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AgyleLoadingSpinner from '../../../../../components/other/agyleLoadingSpinner';
import HistoryFormButton from '../../../../../components/forms/historyFormButton';
import {useTranslation} from 'react-i18next';
import AgyleButton from '../../../../../components/ui/buttons/agyleButton';
import moment from 'moment';
import {getConfig} from '../../../../../util/configUtil';
import clsx from 'clsx';
import CancelIcon from '@mui/icons-material/Cancel';

const useStyles = makeStyles((theme) => ({
    test: {
        marginLeft: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    formList: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
    },
    formBox: {
        width: '100%',
        display: 'flex',
        gap: 25,
        padding: 10,
        borderBottom: `1px solid ${theme.palette.background.paperHover}`,
        '&.last': {
            borderBottom: 'none',
        },
    },
    right: {
        marginLeft: 'auto',
    },
    formText: {
        fontSize: 'small',
    },
    bold: {
        fontWeight: 'bold',
    },
    strikeThrough: {
        textDecoration: 'line-through',
        color: theme.palette.text.disabled,
    },
}));

const AssessmentFormFilled = ({assessment, patient, disableSave}) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // Config
    const dateFormat = getConfig('defaultDateTimeFormat');

    // Selectors
    const patientAddData = useSelector(additionalData);
    const patientType = useSelector(ctxPatientType);
    const patientAgeType = useSelector(ctxPatientAgeType);

    const [expanded, setExpanded] = useState(false);

    const [formContainer, setFormContainer] = useState(null);
    const [isFilled, setIsFilled] = useState(false);
    const [formData, setFormData] = useState([]);

    const [isLoadingIsHistory, setLoadingIsHistory] = useState(false);
    const [isMultiple, setIsMultiple] = useState(false);

    useEffect(() => {
        if (assessment) {
            const findForm = contextFormsList.find(form => form.id === assessment.code);
            if (findForm) {
                setFormContainer(findForm);
                setIsMultiple(findForm?.options?.isMultipleForm || false);
            }
        }
    }, [assessment]);

    useEffect(() => {
        if (formContainer && assessment?.metadata?.path) {
            try {
                const isAddData = formContainer.options.dataSaveDestination === 'patientData';
                const {path} = assessment.metadata;
                const data = _.get((isAddData ? patientAddData : patient), path.filled);
                if (data) {
                    let filteredData = _.isArray(data) ? data : [data];
                    if (isAddData) {
                        filteredData = _.filter(filteredData, f => {
                            const activityId = f?.attendanceId;
                            const date = _.get(f, path.date);

                            if (activityId) {
                                return patient?.admission?.__id === activityId;
                            }
                            if (date) {
                                const startDate = moment(patient?.admission?.startDate);
                                const isActive = _.isNil(patient?.admission?.endDate);
                                const endDate = isActive
                                    ? moment()
                                    : moment(patient?.admission?.startDate);

                                return moment(date).isBetween(startDate, endDate, null, '[]');
                            }
                            return null;
                        });
                    }
                    setIsFilled(!_.isEmpty(filteredData));
                    setFormData(isAddData ? _.orderBy(filteredData, f => moment(_.get(f, path.date)), 'desc') : filteredData);
                }
            } catch (e) {
                console.error(e);
            }
        }
    }, [patient, patientAddData, formContainer, assessment]);

    const handleExpand = (e) => {
        const disableSelect = e.target.closest('.disabled-options') !== null;
        if (!disableSelect) {
            setExpanded(!expanded);
        }
    };

    const getCounter = () => {
        const filteredData = formData.filter(f => !f.canceled);

        return (
            <div className={classes.divRow} style={{gap: 15, marginRight: 5}}>
                {(isMultiple && isLoadingIsHistory) &&
                    <AgyleLoadingSpinner disableText spinnerSize={14} className={classes.loadingSpinner}/>}

                {(isMultiple && formContainer) &&
                    <HistoryFormButton
                        disabled={disableSave}
                        openFormPath={formContainer.id}
                        isPatientAddData={formContainer?.options?.dataSaveDestination !== 'piw'}
                        multiplePath={assessment?.metadata?.path?.filled}
                        setLoading={setLoadingIsHistory}
                    />
                }

                <div>
                    <div className={classes.divRow} style={{gap: 5}}>
                        {filteredData.length >= 1 &&
                            <>
                                {filteredData.length || 0}
                                <CheckCircleIcon size="small" color="success"/>
                            </>
                        }
                    </div>
                </div>
            </div>
        );
    };

    const handleReview = (data) => {
        if (isMultiple) {
            dispatch(openPatientForm(`$multi_${formContainer.id}_$id_${data.id || data.__id}`));
        } else {
            dispatch(openPatientForm(formContainer.id));
        }
    };

    const checkData = (data, paths) => {
        const getData = _.get(data, paths[0]);
        return getData || _.get(data, paths[1]);
    };

    const getButton = (data, index) => {
        const {path} = assessment.metadata;

        const user = _.isArray(path.user) ? checkData(data, path.user) : _.get(data, path.user);
        const date = _.isArray(path.user) ? checkData(data, path.date) : _.get(data, path.date);
        const isCanceled = data?.canceled;

        return (
            <div
                key={index}
                className={clsx(
                    classes.formBox,
                    formData?.length - 1 === index && 'last',
                )}
            >
                <div>
                    {isCanceled && <CancelIcon fontSize="large" color="error"/>}
                    {!isCanceled && <CheckCircleIcon fontSize="large" color="success"/>}
                </div>

                <div className={clsx(isCanceled && classes.strikeThrough)}>
                    <div className={classes.formText}>
                        {`${t('Created by')}: `}
                        <span className={classes.bold}>
                            {user || '-'}
                        </span>
                    </div>

                    <div className={classes.formText}>
                        {`${t('Created')}: `}
                        <span className={classes.bold}>
                            {date ? moment(date).format(dateFormat) : '-'}
                        </span>
                    </div>
                </div>

                <div className={classes.right}>
                    <AgyleButton
                        onClick={() => handleReview(data)}
                        colour="primary"
                        width={100}
                    >
                        {t('Preview')}
                    </AgyleButton>
                </div>
            </div>
        );
    };

    if (!isFilled) return null;

    return (
        <AgyleAccordion
            expanded={expanded}
            label={getFormCustomTitle(formContainer, patientType, patientAgeType)}
            onChange={handleExpand}
            secondLabel={getCounter()}
            secondHeadingStyle={classes.test}
        >
            <div className={classes.formList}>
                {formData?.map((data, index) => getButton(data, index))}
            </div>
        </AgyleAccordion>
    );
};

export default AssessmentFormFilled;